import {
  Stack,
  TextInput,
  Textarea,
  Group,
  Button,
  ActionIcon,
  Card,
  Text,
  Select,
  Divider,
  Title,
  Table,
  Tooltip,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";

const COMMON_LANGUAGES = [
  { value: "en", label: "English (en)" },
  { value: "de", label: "German (de)" },
  { value: "fr", label: "French (fr)" },
  { value: "it", label: "Italian (it)" },
  { value: "es", label: "Spanish (es)" },
  { value: "nl", label: "Dutch (nl)" },
  { value: "pt", label: "Portuguese (pt)" },
  { value: "sv", label: "Swedish (sv)" },
  { value: "no", label: "Norwegian (no)" },
  { value: "da", label: "Danish (da)" },
  { value: "pl", label: "Polish (pl)" },
];

function GuidelineCard({ guideline, onChange, onRemove }) {
  const updateField = (field, value) => onChange({ ...guideline, [field]: value });

  const updateGlossary = (next) => onChange({ ...guideline, glossary: next });

  const addGlossaryEntry = () =>
    updateGlossary([...(guideline.glossary || []), { preferred: "", avoid: "", notes: "" }]);

  const removeGlossaryEntry = (index) =>
    updateGlossary(guideline.glossary.filter((_, i) => i !== index));

  const updateGlossaryEntry = (index, field, value) =>
    updateGlossary(
      guideline.glossary.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Text fw={600}>{guideline.languageCode}</Text>
          {guideline.countryCode && <Text c="dimmed">({guideline.countryCode})</Text>}
        </Group>
        <Tooltip label="Remove this language">
          <ActionIcon variant="subtle" color="red" onClick={onRemove}>
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Stack gap="sm">
        <Group grow>
          <TextInput
            label="Country code (optional)"
            description="ISO 3166 — e.g. DE, AT, CH for German variants"
            placeholder="DE"
            value={guideline.countryCode || ""}
            onChange={(e) => updateField("countryCode", e.currentTarget.value || null)}
          />
        </Group>

        <Textarea
          label="Formality"
          description='How to address the reader. e.g. "Sie throughout, never du"'
          autosize
          minRows={2}
          value={guideline.formalityNotes || ""}
          onChange={(e) => updateField("formalityNotes", e.currentTarget.value)}
        />

        <Textarea
          label="Voice"
          description="Tone descriptors — confident but not boastful, warm and direct, etc."
          autosize
          minRows={2}
          value={guideline.voiceNotes || ""}
          onChange={(e) => updateField("voiceNotes", e.currentTarget.value)}
        />

        <Textarea
          label="Style"
          description="Catch-all soft-prompting — sentence length, idioms to use/avoid, regional notes."
          autosize
          minRows={2}
          value={guideline.styleNotes || ""}
          onChange={(e) => updateField("styleNotes", e.currentTarget.value)}
        />

        <div>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>Glossary</Text>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={addGlossaryEntry}
            >
              Add term
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            Preferred terms the brand uses. Soft prompting only — no enforcement.
          </Text>
          {guideline.glossary?.length > 0 ? (
            <Table withTableBorder withColumnBorders verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Use</Table.Th>
                  <Table.Th>Avoid</Table.Th>
                  <Table.Th>Notes</Table.Th>
                  <Table.Th style={{ width: 40 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {guideline.glossary.map((entry, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="Niederlassung"
                        value={entry.preferred || ""}
                        onChange={(e) => updateGlossaryEntry(i, "preferred", e.currentTarget.value)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="Filiale"
                        value={entry.avoid || ""}
                        onChange={(e) => updateGlossaryEntry(i, "avoid", e.currentTarget.value)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="Sounds retail/shop-like"
                        value={entry.notes || ""}
                        onChange={(e) => updateGlossaryEntry(i, "notes", e.currentTarget.value)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => removeGlossaryEntry(i)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="xs" c="dimmed">No glossary entries yet.</Text>
          )}
        </div>
      </Stack>
    </Card>
  );
}

export default function ContentGuidelinesEditor({
  guidelines,
  onChange,
  defaultTranslationMode,
  onTranslationModeChange,
  brand,
}) {
  const [newLanguage, setNewLanguage] = useState(null);

  const usedLanguages = new Set(
    (guidelines || []).map((g) => `${g.languageCode}|${g.countryCode || ""}`)
  );

  const suggestedLanguages = (brand?.targetLanguages || [])
    .filter((lang) => !usedLanguages.has(`${lang}|`))
    .map((lang) => ({
      value: lang,
      label: COMMON_LANGUAGES.find((c) => c.value === lang)?.label || lang,
    }));

  const otherLanguages = COMMON_LANGUAGES.filter(
    (c) => !usedLanguages.has(`${c.value}|`) && !suggestedLanguages.some((s) => s.value === c.value)
  );

  const languageOptions = [
    ...(suggestedLanguages.length > 0
      ? [{ group: "Brand target languages", items: suggestedLanguages }]
      : []),
    { group: "Other", items: otherLanguages },
  ];

  const addGuideline = () => {
    if (!newLanguage) return;
    onChange([
      ...(guidelines || []),
      {
        languageCode: newLanguage,
        countryCode: null,
        formalityNotes: "",
        voiceNotes: "",
        styleNotes: "",
        glossary: [],
      },
    ]);
    setNewLanguage(null);
  };

  const updateGuideline = (index, next) =>
    onChange(guidelines.map((g, i) => (i === index ? next : g)));

  const removeGuideline = (index) =>
    onChange(guidelines.filter((_, i) => i !== index));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <div>
          <Title order={5}>Content Guidelines</Title>
          <Text size="xs" c="dimmed">
            Per-language voice, formality, and glossary used by content generation and
            LocalizedRewrite-mode translation.
          </Text>
        </div>
      </Group>

      <Group align="flex-end" gap="xs">
        <Select
          label="Add language"
          placeholder="Pick a language"
          data={languageOptions}
          value={newLanguage}
          onChange={setNewLanguage}
          searchable
          allowDeselect
          style={{ flex: 1 }}
        />
        <Button leftSection={<IconPlus size={14} />} onClick={addGuideline} disabled={!newLanguage}>
          Add
        </Button>
      </Group>

      {guidelines?.length > 0 ? (
        <Stack gap="md">
          {guidelines.map((g, i) => (
            <GuidelineCard
              key={`${g.languageCode}-${g.countryCode || ""}-${i}`}
              guideline={g}
              onChange={(next) => updateGuideline(i, next)}
              onRemove={() => removeGuideline(i)}
            />
          ))}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">
          No content guidelines yet. Add a language above to start.
        </Text>
      )}

      <Divider label="Translation behaviour" labelPosition="left" mt="md" />

      <Select
        label="Default translation mode"
        description={
          <>
            <strong>Translate</strong> faithfully renders the source draft in the target language. {" "}
            <strong>LocalizedRewrite</strong> writes fresh in the target language using the brief +
            source as facts only, applying the target-language guideline.
          </>
        }
        data={[
          { value: "Translate", label: "Translate (faithful render)" },
          { value: "LocalizedRewrite", label: "Localized rewrite (recommended for marketing copy)" },
        ]}
        value={defaultTranslationMode || "Translate"}
        onChange={onTranslationModeChange}
        allowDeselect={false}
      />
    </Stack>
  );
}
